import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Heading,
  Image,
  useColorModeValue,
  Flex,
  Icon,
  Link,
} from "@chakra-ui/react";
import { FiMapPin, FiPhone, FiMail } from "react-icons/fi";

const OfficeLocation = ({ data }) => {
  const [selectedOffice, setSelectedOffice] = useState(0); // Use index instead of ID
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // For slideshow

  const bgColor = useColorModeValue("white", "gray.800");
  const sidebarBg = useColorModeValue("#f8fafc", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const selectedBg = useColorModeValue("#1e40af", "#000B8C");
  const cardBg = useColorModeValue("white", "gray.600");
  const borderColor = useColorModeValue("#e2e8f0", "gray.600");

  const offices = Array.isArray(data) ? data : [];
  const currentOffice = offices[selectedOffice] || offices[0];

  // Get images array - the backend sends 'images' array from our service
  const officeImages = currentOffice?.images || [];
  
  // Auto-advance slideshow every 4 seconds
  useEffect(() => {
    if (officeImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % officeImages.length
        );
      }, 4000); // 4 seconds

      return () => clearInterval(interval);
    }
  }, [officeImages.length, selectedOffice]);

  // Reset image index when office changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedOffice]);

  if (!offices.length) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text textAlign="center" color="gray.500">
          No office locations available from API
        </Text>
      </Container>
    );
  }

  const handleOfficeSelect = (index) => {
    setSelectedOffice(index);
  };

  const OfficeIcon = ({ isSelected }) => (
    <Box
      width="40px"
      height="40px"
      bg={isSelected ? "white" : "gray.100"}
      borderRadius="md"
      display="flex"
      alignItems="center"
      justifyContent="center"
      border="1px solid"
      borderColor={isSelected ? "transparent" : borderColor}
    >
      <Image
        src="/png/office_icon.svg"
        alt="Office icon"
        boxSize="20px"
        objectFit="contain"
        filter={isSelected ? "none" : "grayscale(40%)"}
        fallbackSrc="/png/office_icon.png"
      />
    </Box>
  );

  return (
    <Container maxW="container.xl" py={8}>
      {/* Page Title */}
      {/* <Text
        fontSize={{ base: "2xl", md: "3xl" }}
        fontWeight="bold"
        color="#000B8C"
        mb={8}
        textAlign="center"
      >
        Office Location
      </Text> */}
      <HStack justify="center" mb={8}>
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "4xl" }}
          fontWeight="bold"
          color="#000B8C"
          lineHeight="1.2"
        >
          Office{" "}
          <Text as="span" color="gray.700">
            Location
          </Text>
        </Heading>
      </HStack>

      <Flex direction={{ base: "column", lg: "row" }} gap={6}>
        {/* Left Sidebar Navigation - Card-based Design */}
        <Box
          width={{ base: "100%", lg: "320px" }}
          bg={sidebarBg}
          borderRadius="lg"
          p={4}
          height="fit-content"
        >
          <VStack spacing={3} align="stretch">
            {offices.map((office, index) => {
              const isSelected = selectedOffice === index;

              return (
                <Box
                  key={office.id || index}
                  as="button"
                  onClick={() => handleOfficeSelect(index)}
                  bg={isSelected ? selectedBg : cardBg}
                  color={isSelected ? "white" : textColor}
                  p={4}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={isSelected ? selectedBg : borderColor}
                  transition="all 0.2s ease"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                    borderColor: isSelected ? selectedBg : "blue.200",
                  }}
                  _active={{
                    transform: "translateY(0px)",
                  }}
                  textAlign="left"
                  width="100%"
                  boxShadow={isSelected ? "md" : "sm"}
                >
                  <HStack spacing={3} align="center">
                    <OfficeIcon isSelected={isSelected} />
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      lineHeight="1.4"
                      flex={1}
                    >
                      {office?.name || "Office"}
                    </Text>
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        </Box>

        {/* Right Content Area */}
        <Box
          flex={1}
          bg={bgColor}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
        >
          <Box p={8}>
            {/* Company Name and Details */}
            <VStack align="start" spacing={6}>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color={textColor}
                textAlign="center"
                width="100%"
              >
                {currentOffice?.name || "Office"}
              </Text>

              {/* Contact Information */}
              <VStack align="center" spacing={4} width="100%">
                <HStack spacing={3}>
                  <Icon as={FiMapPin} color="gray.500" boxSize={4} />
                  <Text color={textColor} fontSize="sm">
                    {currentOffice?.location || "Location not available"}
                  </Text>
                </HStack>

                <HStack>
                  {currentOffice?.phone && (
                    <HStack spacing={3}>
                      <Icon as={FiPhone} color="gray.500" boxSize={4} />
                      <Link
                        href={`tel:${currentOffice.phone}`}
                        color={textColor}
                        fontSize="sm"
                        _hover={{ textDecoration: "underline" }}
                      >
                        {currentOffice.phone}
                      </Link>
                    </HStack>
                  )}

                  {currentOffice?.email && (
                    <HStack spacing={3}>
                      <Icon as={FiMail} color="gray.500" boxSize={4} />
                      <Link
                        href={`mailto:${currentOffice.email}`}
                        color={textColor}
                        fontSize="sm"
                        _hover={{ textDecoration: "underline" }}
                      >
                        {currentOffice.email}
                      </Link>
                    </HStack>
                  )}
                </HStack>
              </VStack>
            </VStack>
          </Box>

          {/* Office Image Slideshow */}
          <Box
            width="100%"
            height="400px"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderTop="1px solid"
            borderColor={borderColor}
            borderBottomRadius="lg"
            position="relative"
            overflow="hidden"
          >
            {officeImages.length > 0 ? (
              officeImages.map((imageUrl, index) => (
                <Image
                  key={index}
                  src={imageUrl || "/png/office_placeholder.png"}
                  alt={`${currentOffice?.name || "Office"} office interior ${index + 1}`}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  borderBottomRadius="lg"
                  fallbackSrc="/png/office_placeholder.png"
                  position="absolute"
                  top="0"
                  left="0"
                  opacity={index === currentImageIndex ? 1 : 0}
                  transition="opacity 0.8s ease-in-out"
                />
              ))
            ) : (
              <Image
                src="/png/office_placeholder.png"
                alt={`${currentOffice?.name || "Office"} office interior`}
                width="100%"
                height="100%"
                objectFit="cover"
                borderBottomRadius="lg"
              />
            )}
            
            {/* Slideshow Indicators - only show if more than 1 image */}
            {officeImages.length > 1 && (
              <HStack
                position="absolute"
                bottom="20px"
                left="50%"
                transform="translateX(-50%)"
                spacing={2}
                bg="blackAlpha.600"
                px={3}
                py={2}
                borderRadius="full"
              >
                {officeImages.map((_, index) => (
                  <Box
                    key={index}
                    width="8px"
                    height="8px"
                    borderRadius="full"
                    bg={index === currentImageIndex ? "white" : "whiteAlpha.500"}
                    cursor="pointer"
                    transition="all 0.3s ease"
                    onClick={() => setCurrentImageIndex(index)}
                    _hover={{
                      bg: index === currentImageIndex ? "white" : "whiteAlpha.700",
                    }}
                  />
                ))}
              </HStack>
            )}
          </Box>
        </Box>
      </Flex>
    </Container>
  );
};

export default OfficeLocation;
